import {
  OffersByCategory,
  RentalOffer,
} from '@silvertours/common-landingpages-view';
import { Carousel, SerpOffer, analytics } from '@silvertours/front-entities';
import { useCallback, useMemo } from 'react';
import { Heading2, Theme, Ui } from '@silvertours/front-shared';
import { PageAttributeType } from '@silvertours/back-domain-page';
import { useMedia } from 'react-use';
import { Container, SerpOffersWrapper } from './SerpOffers.styles';
import { useSubmitInitialSearch } from '../SearchFormLegacy';

export type Props = {
  content: OffersByCategory;
  translations: {
    airConditioning: string;
    prevText: string;
    nextText: string;
    disclaimertext: string;
    pricePrefix: string;
    priceSuffix: string;
    serpTitle: string;
    serpBadge?: string;
    seats: string;
    doors: string;
    transmission: string;
    transmissionManual: string;
    transmissionAutomatic: string;
    searchButtonText: string;
    ariaLabel: string;
    carTypes: {
      [Key: string]: string;
      compact: string;
      convertible: string;
      medium: string;
      mini: string;
      premium: string;
      small: string;
      sport: string;
      station: string;
      suv: string;
      transporter: string;
      upper: string;
      van: string;
    };
  };
  searchQuery?: string;
  isAirport?: boolean;
  background?: 'mint';
};

const MAX_OFFERS = 12;

const MintBackground = ({ children }: React.PropsWithChildren<object>) => (
  <Ui.PlaneBackground backgroundStyle="mint">{children}</Ui.PlaneBackground>
);

const SerpOffers = ({
  content,
  translations: {
    airConditioning,
    carTypes,
    prevText,
    nextText,
    disclaimertext,
    pricePrefix,
    priceSuffix,
    serpTitle,
    serpBadge,
    seats: seatsTranslation,
    doors: doorsTranslation,
    transmission: transmissionTranslation,
    transmissionAutomatic,
    transmissionManual,
    searchButtonText,
    ariaLabel,
  },
  searchQuery,
  isAirport = false,
  background,
}: Props) => {
  const offers = useMemo(() => {
    const result: (RentalOffer & { categorySearchQuery: string })[] = [];
    Object.values(content).forEach(categoryOffers => {
      categoryOffers.offers.forEach(offer => {
        // skip offers with car_image.php usage for now,
        // as there is a chance that images are not available
        // anymore after a certain time
        if (
          (offer.attributes !== null || offer.category === 'transporter') &&
          !/car_image\.php/.test(offer.originalImage)
        ) {
          result.push({
            ...offer,
            categorySearchQuery: categoryOffers.searchQuery,
          });
        }
      });
    });
    return result.toSorted((a, b) => a.price - b.price).slice(0, MAX_OFFERS);
  }, [content]);

  const { makeSearchHandler } = useSubmitInitialSearch();
  const onSlideChange = (direction: 'forward' | 'backward') => {
    analytics.gtm.trackOffersSliderButtonClick(direction);
    analytics.generic.trackOfferBoxSliderButtonClick(direction);
  };

  const onSlideDrag = useCallback((offset: number) => {
    analytics.gtm.trackOffersSliderDragged(offset);
    analytics.generic.trackOfferBoxSliderDragged(offset);
  }, []);

  const onMouseWheel = useCallback((offset: number) => {
    analytics.gtm.trackOffersSliderMouseWheel(offset);
    analytics.generic.trackOfferBoxSliderMouseWheel(offset);
  }, []);

  const CarouselContainer = Container;

  const isMobile = useMedia(
    `(max-width: ${Theme.getBreakpoint('lg')}px)`,
    true,
  );

  const visibleItemsAmount = isMobile ? 1 : 3;

  if (!offers.length) {
    return null;
  }

  const Wrapper = background === 'mint' ? MintBackground : SerpOffersWrapper;

  return (
    <Wrapper>
      <Ui.SectionTitle text={serpBadge} type="brand-green" />
      <Heading2>{serpTitle}</Heading2>
      <CarouselContainer
        as={Carousel}
        hasItemsToReveal={offers.length > visibleItemsAmount}
        showSlideBackground={false}
        shouldLoop
        snap
        prevText={prevText}
        nextText={nextText}
        captionText={disclaimertext}
        onSlideChange={onSlideChange}
        onSlideDrag={onSlideDrag}
        onMouseWheel={onMouseWheel}
        label={ariaLabel}
      >
        {offers.map(offer => {
          const handleClick = () => {
            const handleSearch = makeSearchHandler({
              cityCode: offer.cityCode,
              cityName: offer.city,
              query: offer.city,
              displayName: offer.city,
              type: PageAttributeType.City,
            });
            if (handleSearch) {
              analytics.gtm.trackOffersSliderOfferClick(offer.id ?? '');
              analytics.generic.trackOfferBoxStartSearch(offer.id ?? '');
              handleSearch(
                [searchQuery, offer.categorySearchQuery]
                  .filter(Boolean)
                  .join('&'),
                {
                  depDate: offer.departureDate.replace(' ', 'T'),
                  destDate: offer.destinationDate.replace(' ', 'T'),
                },
              );
            }
          };

          let transmission = '';
          let seats = 2;
          let doors = '';
          let aircon = false;

          if (offer.attributes !== null) {
            transmission = offer.attributes!.transmission;
            seats = offer.attributes!.seats;
            doors = offer.attributes!.doors;
            aircon = offer.attributes!.airConditioning;
          }

          return (
            <SerpOffer
              key={offer.id}
              onClick={handleClick}
              image={offer.originalImage}
              fallbackImage={offer.image}
              category={offer.category}
              carCategory={carTypes[offer.category]}
              carName={offer.carName}
              transmission={transmission}
              seats={seats}
              doors={doors}
              airConditioning={aircon}
              city={offer.city}
              price={offer.price}
              currency="EUR"
              isAirport={isAirport}
              translations={{
                seats: seatsTranslation,
                doors: doorsTranslation,
                airConditioning,
                transmission: transmissionTranslation,
                transmissionAutomatic,
                transmissionManual,
                pricePrefix,
                priceSuffix,
                searchButtonText: searchButtonText.replace(
                  '%LOCATION%',
                  offer.city,
                ),
              }}
            />
          );
        })}
      </CarouselContainer>
    </Wrapper>
  );
};

export { SerpOffers as SerpOffersModule };
